// vim: set ts=2 sts=2 sw=2 et:
//
// This file is part of OpenLifter, simple Powerlifting meet software.
// Copyright (C) 2019 The OpenPowerlifting Project.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as
// published by the Free Software Foundation, either version 3 of the
// License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

// WRPF Pesaje: search and status filters for the Weigh-ins page.

import { isEntryPresent } from "../entry";
import { Entry } from "../../types/dataTypes";

export type WeighInFilter = "all" | "pending" | "done";
export type PresenceFilter = "all" | "present" | "absent";

export type WeighinsFilters = {
  query: string;
  weighIn: WeighInFilter;
  presence: PresenceFilter;
};

export const emptyWeighinsFilters = (): WeighinsFilters => ({
  query: "",
  weighIn: "all",
  presence: "all",
});

export const hasActiveWeighinsFilters = (filters: WeighinsFilters): boolean => {
  return filters.query.trim() !== "" || hasStatusFilter(filters);
};

export const hasStatusFilter = (filters: WeighinsFilters): boolean => {
  return filters.weighIn !== "all" || filters.presence !== "all";
};

// Lowercase and without accents, so "jose" finds "José".
const normalizeText = (text: string): string => {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
};

// Only letters and digits, so "30.111.222" finds member ID "30111222".
const compactId = (text: string): string => normalizeText(text).replace(/[^0-9a-z]/g, "");

export const matchesWeighinsQuery = (entry: Pick<Entry, "name" | "memberId">, query: string): boolean => {
  const q = normalizeText(query);
  if (q === "") return true;
  if (normalizeText(entry.name).includes(q)) return true;
  const id = compactId(q);
  return id !== "" && compactId(entry.memberId).includes(id);
};

export const matchesWeighinsStatus = (entry: Entry, filters: WeighinsFilters): boolean => {
  const weighedIn = entry.weighedIn === true;
  if (filters.weighIn === "pending" && weighedIn) return false;
  if (filters.weighIn === "done" && !weighedIn) return false;

  const present = isEntryPresent(entry);
  if (filters.presence === "present" && !present) return false;
  if (filters.presence === "absent" && present) return false;
  return true;
};

export const filterWeighinsEntries = (entries: ReadonlyArray<Entry>, filters: WeighinsFilters): Entry[] => {
  return entries.filter((e) => matchesWeighinsQuery(e, filters.query) && matchesWeighinsStatus(e, filters));
};
